import React, { useContext, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { device } from "../lib/breakpoints";
import { APP_LOGO, APP_NAME, TAGLINE } from "../helpers/constants";
import { AuthContext } from "../context/AuthProvider";
import { validateInviteCode, registerReferral } from "../lib/api/inviteAPI";
import { message, Spin } from "antd";
import SEO from "../components/SEO";
import IntakeModal from "../components/IntakeModal";
import { getPostLoginHomePath } from "../helpers/accountProduct";

const Signup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userInfo, isUserInfoLoading } = useContext(AuthContext);

  const inviteCode = searchParams.get("invite");

  const [inviteData, setInviteData] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [showIntakeModal, setShowIntakeModal] = useState(false);

  // `/signup` is invite-only. Bare `/signup` (no ?invite=) redirects to home with signup modal.
  useEffect(() => {
    if (!inviteCode) {
      navigate({ pathname: "/", search: "?signup=" }, { replace: true });
    }
  }, [inviteCode, navigate]);

  // Logged-in users with a profile go to the app. No profile + invite link: stay (intake). No profile + no invite: home signup.
  useEffect(() => {
    if (!user || isUserInfoLoading) return;
    if (userInfo) {
      navigate(getPostLoginHomePath(userInfo), { replace: true });
      return;
    }
    if (!inviteCode) {
      navigate({ pathname: "/", search: "?signup=" }, { replace: true });
    }
  }, [user, userInfo, isUserInfoLoading, navigate, inviteCode]);

  // Validate invite code and auto-open intake modal
  useEffect(() => {
    if (!inviteCode) return;

    setInviteLoading(true);
    validateInviteCode(inviteCode)
      .then((data) => {
        setInviteData(data);
        if (data.success && data.valid) {
          setTimeout(() => {
            setShowIntakeModal(true);
          }, 1000);
        } else {
          message.error("Invalid invite link");
        }
      })
      .catch((error) => {
        console.error("Error validating invite:", error);
        message.error("Could not validate invite link");
        setInviteData({ valid: false, expired: false });
      })
      .finally(() => {
        setInviteLoading(false);
      });
  }, [inviteCode]);

  const handleIntakeCancel = () => {
    setShowIntakeModal(false);
    navigate("/");
  };

  if (!inviteCode) {
    return (
      <SignupContainer>
        <SEO
          title={`Sign Up | ${APP_NAME} - ${TAGLINE}`}
          description={`Sign up for ${APP_NAME}`}
          keywords="signup"
          imageAlt={`${APP_NAME} signup`}
          category="Authentication"
          tags={["signup"]}
        />
        <SignupContent>
          <LoadingContainer>
            <Spin size="large" />
            <LoadingText>Redirecting…</LoadingText>
          </LoadingContainer>
        </SignupContent>
      </SignupContainer>
    );
  }

  return (
    <SignupContainer>
      <SEO
        title={`Sign Up | ${APP_NAME} - ${TAGLINE}`}
        description={`Sign up for ${APP_NAME} and start managing your rental properties with AI-powered tools. Get started with our comprehensive property management platform.`}
        keywords={`${APP_NAME} signup, property management signup, landlord software signup, rental property management signup`}
        imageAlt={`${APP_NAME} signup page`}
        category="Authentication"
        tags={[
          "Clumse signup",
          "property management signup",
          "landlord software signup",
          "rental property management signup",
        ]}
      />

      <SignupContent>
        <LogoWrapper onClick={() => navigate("/")}>
          <img src={APP_LOGO} alt={APP_NAME} />
        </LogoWrapper>

        {inviteLoading ? (
          <LoadingContainer>
            <Spin size="large" />
            <LoadingText>Validating your invite...</LoadingText>
          </LoadingContainer>
        ) : inviteData?.valid ? (
          <InviteSuccessContainer>
            <InviteTitle>You've Been Invited!</InviteTitle>
            <InviteMessage>
              <strong>{inviteData.referrerName}</strong> has invited you to join{" "}
              {APP_NAME}
            </InviteMessage>
            <InviteBenefit>
              🎁 You'll both get a <strong>FREE MONTH</strong> when you complete
              your first payment!
            </InviteBenefit>
            <GetStartedButton onClick={() => setShowIntakeModal(true)}>
              Get Started
            </GetStartedButton>
          </InviteSuccessContainer>
        ) : (
          <InviteErrorContainer>
            <InviteIcon>⚠️</InviteIcon>
            <InviteTitle>Invalid Invite</InviteTitle>
            <InviteMessage>
              This invite link is invalid or has expired
            </InviteMessage>
            <BackButton onClick={() => navigate("/")}>
              Go to Homepage
            </BackButton>
          </InviteErrorContainer>
        )}
      </SignupContent>

      {/* Intake Modal */}
      <IntakeModal
        isOpen={showIntakeModal}
        onClose={handleIntakeCancel}
        marketingSignup
        pricingProduct="procurement"
      />
    </SignupContainer>
  );
};

export default Signup;

// Styled Components
const SignupContainer = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  background-image: var(--gradient-background);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  @media ${device.mobile} {
    padding: 1rem;
  }
`;

const SignupContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  max-width: 500px;
  width: 100%;
  text-align: center;

  @media ${device.mobile} {
    padding: 2rem;
    border-radius: 20px;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  cursor: pointer;

  img {
    height: 60px;
    width: auto;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
`;

const LoadingText = styled.div`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: var(--color-text-secondary);
`;

const InviteSuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
`;

const InviteErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem 0;
`;

const InviteIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 0.5rem;
`;

const InviteTitle = styled.h2`
  font-family: var(--font-primary);
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-text);
  margin: 0;
  letter-spacing: -0.02em;

  @media ${device.mobile} {
    font-size: 1.75rem;
  }
`;

const InviteMessage = styled.div`
  font-family: var(--font-secondary);
  font-size: 1.1rem;
  color: var(--color-text);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const InviteBenefit = styled.div`
  font-family: var(--font-secondary);
  font-size: 1rem;
  color: var(--color-primary);
  font-weight: 600;
  background: rgba(44, 95, 45, 0.1);
  padding: 1rem;
  border-radius: 12px;
  border: 2px solid rgba(44, 95, 45, 0.2);
  margin-bottom: 1rem;
`;

const OpeningText = styled.div`
  font-family: var(--font-secondary);
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  font-style: italic;
`;

const BackButton = styled.button`
  background: var(--gradient-primary);
  border: none;
  border-radius: 12px;
  padding: 0.75rem 1.5rem;
  color: white;
  font-family: var(--font-secondary);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(44, 95, 45, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const GetStartedButton = styled.button`
  background: var(--gradient-primary);
  border: none;
  border-radius: 16px;
  padding: 1rem 2rem;
  color: white;
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 6px 20px rgba(44, 95, 45, 0.3);
  margin-top: 1rem;
  letter-spacing: -0.01em;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(44, 95, 45, 0.4);
  }

  &:active {
    transform: translateY(-1px);
  }
`;
